import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PageHeader } from '@/components/layout/PageHeader';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import type {
  PaymentHistoryDetail,
  PaymentHistoryRecord,
} from '@claude-code-api/shared';

export default function PaymentsPage() {
  const { t } = useTranslation('payments');
  const stripeAssetBase =
    typeof document !== 'undefined'
      ? new URL('.', document.baseURI).pathname
      : '/';
  const stripeWordmarkLight = `${stripeAssetBase}stripe/wordmark-slate-small.png`;
  const stripeWordmarkDark = `${stripeAssetBase}stripe/wordmark-white-small.png`;
  const [records, setRecords] = useState<PaymentHistoryRecord[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [selectedDetail, setSelectedDetail] =
    useState<PaymentHistoryDetail | null>(null);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await fetch(`/api/payments/history?page=${page}&limit=20`, {
          credentials: 'include',
        });
        if (!res.ok) return;
        const payload = await res.json();
        setRecords(payload?.data?.records || []);
        setTotalPages(Number(payload?.data?.pagination?.totalPages || 1));
      } finally {
        setLoading(false);
      }
    };
    void load();
  }, [page]);

  if (loading) {
    return (
      <div className="space-y-6">
        <PageHeader title={t('title')} description={t('description')} />
        <Skeleton className="h-56 rounded-lg" />
      </div>
    );
  }

  const openDetail = async (recordId: string) => {
    setDetailLoading(true);
    setDetailOpen(true);
    try {
      const res = await fetch(`/api/payments/history/${recordId}`, {
        credentials: 'include',
      });
      if (!res.ok) {
        setSelectedDetail(null);
        return;
      }
      const payload = await res.json();
      setSelectedDetail(payload?.data || null);
    } finally {
      setDetailLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <PageHeader title={t('title')} description={t('description')} />

      {records.length === 0 ? (
        <div className="rounded-lg border bg-surface-0 p-6 text-sm text-muted-foreground">
          {t('empty')}
        </div>
      ) : (
        <div className="space-y-4">
          <div className="border rounded-lg overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t('table.date')}</TableHead>
                  <TableHead>{t('table.type')}</TableHead>
                  <TableHead>{t('table.item')}</TableHead>
                  <TableHead className="text-right">
                    {t('table.amountUsd')}
                  </TableHead>
                  <TableHead className="text-right">
                    {t('table.credits')}
                  </TableHead>
                  <TableHead>{t('table.status')}</TableHead>
                  <TableHead className="text-right">
                    {t('table.details')}
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {records.map((record) => (
                  <TableRow key={record.id}>
                    <TableCell>
                      {new Date(record.createdAt).toLocaleString()}
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline">
                        {record.kind === 'subscription'
                          ? t('table.subscription')
                          : t('table.creditTopUp')}
                      </Badge>
                    </TableCell>
                    <TableCell className="font-medium">
                      {record.title}
                    </TableCell>
                    <TableCell className="text-right">
                      {record.amountUsd !== null
                        ? `$${Number(record.amountUsd).toFixed(2)}`
                        : '-'}
                    </TableCell>
                    <TableCell className="text-right">
                      {Number(record.credits).toLocaleString()}
                    </TableCell>
                    <TableCell>
                      <Badge>{record.status}</Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          void openDetail(record.id);
                        }}
                      >
                        {t('table.view')}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page <= 1}
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <span className="text-sm text-muted-foreground">
                {t('pagination', { page, totalPages })}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page >= totalPages}
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          )}
        </div>
      )}

      <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('detail.title')}</DialogTitle>
            <DialogDescription>{t('description')}</DialogDescription>
          </DialogHeader>
          {detailLoading ? (
            <div className="space-y-2">
              <Skeleton className="h-5 w-full" />
              <Skeleton className="h-5 w-4/5" />
              <Skeleton className="h-5 w-2/3" />
            </div>
          ) : !selectedDetail ? (
            <p className="text-sm text-muted-foreground">
              {t('detail.loadFailed')}
            </p>
          ) : (
            <div className="space-y-3 text-sm">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <p className="text-muted-foreground">{t('detail.type')}</p>
                  <p className="font-medium">
                    {selectedDetail.kind === 'subscription'
                      ? t('table.subscription')
                      : t('table.creditTopUp')}
                  </p>
                </div>
                <div>
                  <p className="text-muted-foreground">{t('detail.status')}</p>
                  <p className="font-medium">{selectedDetail.status}</p>
                </div>
                <div>
                  <p className="text-muted-foreground">{t('detail.amount')}</p>
                  <p className="font-medium">
                    {selectedDetail.amountUsd !== null
                      ? `$${Number(selectedDetail.amountUsd).toFixed(2)}`
                      : '-'}
                  </p>
                </div>
                <div>
                  <p className="text-muted-foreground">{t('detail.credits')}</p>
                  <p className="font-medium">
                    {Number(selectedDetail.credits).toLocaleString()}
                  </p>
                </div>
              </div>

              <div>
                <p className="text-muted-foreground">{t('detail.item')}</p>
                <p className="font-medium">{selectedDetail.title}</p>
              </div>

              <div>
                <p className="text-muted-foreground">{t('detail.createdAt')}</p>
                <p className="font-medium">
                  {new Date(selectedDetail.createdAt).toLocaleString()}
                </p>
              </div>

              {selectedDetail.periodStart && selectedDetail.periodEnd && (
                <div>
                  <p className="text-muted-foreground">
                    {t('detail.billingPeriod')}
                  </p>
                  <p className="font-medium">
                    {new Date(selectedDetail.periodStart).toLocaleDateString()}{' '}
                    - {new Date(selectedDetail.periodEnd).toLocaleDateString()}
                  </p>
                </div>
              )}

              <div className="flex flex-wrap gap-2">
                {selectedDetail.stripe.receiptUrl && (
                  <Button size="sm" variant="outline" asChild>
                    <a
                      href={selectedDetail.stripe.receiptUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-2"
                    >
                      <img
                        src={stripeWordmarkLight}
                        alt="Stripe"
                        className="h-4 w-auto shrink-0 object-contain dark:hidden"
                      />
                      <img
                        src={stripeWordmarkDark}
                        alt="Stripe"
                        className="hidden h-4 w-auto shrink-0 object-contain dark:block"
                      />
                      {t('detail.receipt')}
                    </a>
                  </Button>
                )}
                {selectedDetail.stripe.hostedInvoiceUrl && (
                  <Button
                    size="sm"
                    className="border-0 bg-[#635BFF] text-white hover:bg-[#5145CD] dark:bg-slate-900 dark:text-white dark:hover:bg-slate-800"
                    asChild
                  >
                    <a
                      href={selectedDetail.stripe.hostedInvoiceUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-2"
                    >
                      <img
                        src={stripeWordmarkDark}
                        alt="Stripe"
                        className="h-4 w-auto shrink-0 object-contain"
                      />
                      {t('detail.invoice')}
                    </a>
                  </Button>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
